import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2, Clock, Globe, Instagram, Mail, MessageCircle, Phone, Send, TriangleAlert, X } from "lucide-react";
import { Seo } from "../components/Seo";
import { useCart } from "../lib/cart";
import { useCurrency } from "../lib/currency";
import { submitEnquiry } from "../lib/api";

const COUNTRIES = ["United States", "United Kingdom", "Canada", "Australia", "European Union", "Other"];
const TYPES = ["Product information", "Order enquiry", "Availability check", "General enquiry"];

const jsonLd = {
  "@context": "https://schema.org",
  "@type": "ContactPage",
  name: "Contact OneCare Pharmacy",
  url: "https://onecarelife.health/contact",
};

export default function Contact() {
  const { items, updateQty, removeItem, subtotal, clear } = useCart();
  const { format } = useCurrency();
  const [form, setForm] = useState({ full_name: "", email: "", country: "", enquiry_type: "Product information", message: "", consent: false });
  const [status, setStatus] = useState("idle");
  const [reference, setReference] = useState("");
  const [error, setError] = useState("");

  const basketMsg = useMemo(
    () =>
      items
        .map((i) => `• ${i.name} ${i.strength || ""} — ${i.pack_size} × ${i.quantity} (${format(i.price * i.quantity)})`)
        .join("\n"),
    [items, format],
  );

  useEffect(() => {
    if (items.length > 0) {
      setForm((f) => ({
        ...f,
        enquiry_type: "Order enquiry",
        message: f.message
          ? `${basketMsg}\n\n${f.message}`
          : `Hello OneCare Pharmacy,\n\nI would like to enquire about:\n${basketMsg}\n\nPlease confirm availability and next steps.`,
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setStatus("sending");
    try {
      const res = await submitEnquiry({
        ...form,
        items: items.map(({ id, name, strength, pack_size, quantity, price }) => ({
          product_id: id,
          name: strength ? `${name} ${strength}` : name,
          pack_size,
          quantity,
          unit_price: price,
        })),
      });
      setReference(res.reference_id || "");
      setStatus("sent");
      clear();
    } catch (err) {
      setStatus("error");
      const detail = err?.response?.data?.detail;
      if (typeof detail === "string") {
        setError(detail);
      } else if (Array.isArray(detail) && detail.length > 0) {
        const field = Array.isArray(detail[0].loc) ? detail[0].loc[detail[0].loc.length - 1] : "";
        const friendly = {
          full_name: "Please enter your full name.",
          email: "Please enter a valid email address.",
          country: "Please select your country or region.",
          enquiry_type: "Please choose an enquiry type.",
          message: "Please write a slightly longer message (at least 5 characters).",
        };
        setError(friendly[field] || "Please check the highlighted fields and try again.");
      } else {
        setError("Something went wrong. Please email us directly at info@onecarelife.health.");
      }
    }
  };

  return (
    <main className="bg-[#F8FAFC]">
      <Seo
        title="Contact OneCare Pharmacy | Customer Support"
        description="Contact OneCare Pharmacy at info@onecarelife.health for product information, catalogue questions and order enquiries."
        path="/contact"
        jsonLd={jsonLd}
      />

      <section className="bg-white pb-14 pt-16 sm:pt-20">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <p className="font-mono text-xs font-semibold tracking-[0.22em] text-emerald-700">CONTACT US</p>
          <h1 className="mt-3 font-display text-3xl font-extrabold tracking-tight text-slate-900 sm:text-4xl lg:text-5xl">
            We're here to help
          </h1>
          <p className="mt-5 max-w-2xl text-base leading-relaxed text-slate-600 sm:text-lg">
            For product information, catalogue questions or order enquiries, contact the OneCare Pharmacy team by
            email. Please do not send unnecessary sensitive medical information by email.
          </p>
        </div>
      </section>

      <section className="py-14">
        <div className="mx-auto grid max-w-7xl gap-10 px-5 lg:grid-cols-[0.85fr_1.15fr] lg:px-8">
          {/* Info column */}
          <div className="space-y-5">
            <div className="rounded-2xl border border-slate-200 bg-white p-7">
              <a href="mailto:info@onecarelife.health" data-testid="contact-email-link" className="flex items-center gap-3 text-slate-900 transition-[color] hover:text-emerald-700">
                <span className="rounded-xl bg-[#D8EFE6] p-3 text-emerald-800"><Mail className="h-5 w-5" /></span>
                <span>
                  <span className="block font-mono text-[11px] tracking-widest text-slate-400">EMAIL</span>
                  <span className="block font-display text-sm font-bold">info@onecarelife.health</span>
                </span>
              </a>
              <a href="tel:+17754169317" data-testid="contact-phone-link" className="mt-5 flex items-center gap-3 text-slate-900 transition-[color] hover:text-emerald-700">
                <span className="rounded-xl bg-[#D8EFE6] p-3 text-emerald-800"><Phone className="h-5 w-5" /></span>
                <span>
                  <span className="block font-mono text-[11px] tracking-widest text-slate-400">CALL US</span>
                  <span className="block font-display text-sm font-bold">+1 (775) 416-9317</span>
                </span>
              </a>
              <a href="https://wa.me/918545884773" target="_blank" rel="noopener noreferrer" data-testid="contact-whatsapp-link" className="mt-5 flex items-center gap-3 text-slate-900 transition-[color] hover:text-emerald-700">
                <span className="rounded-xl bg-[#D8EFE6] p-3 text-emerald-800"><MessageCircle className="h-5 w-5" /></span>
                <span>
                  <span className="block font-mono text-[11px] tracking-widest text-slate-400">WHATSAPP</span>
                  <span className="block font-display text-sm font-bold">+91 85458 84773</span>
                </span>
              </a>
              <a href="https://www.instagram.com/onecarellife" target="_blank" rel="noopener noreferrer" data-testid="contact-instagram-link" className="mt-5 flex items-center gap-3 text-slate-900 transition-[color] hover:text-emerald-700">
                <span className="rounded-xl bg-[#D8EFE6] p-3 text-emerald-800"><Instagram className="h-5 w-5" /></span>
                <span>
                  <span className="block font-mono text-[11px] tracking-widest text-slate-400">INSTAGRAM</span>
                  <span className="block font-display text-sm font-bold">@onecarellife</span>
                </span>
              </a>
              <div className="mt-5 flex items-center gap-3 text-slate-900">
                <span className="rounded-xl bg-[#D8EFE6] p-3 text-emerald-800"><Globe className="h-5 w-5" /></span>
                <span>
                  <span className="block font-mono text-[11px] tracking-widest text-slate-400">WEBSITE</span>
                  <span className="block font-display text-sm font-bold">onecarelife.health</span>
                </span>
              </div>
              <div className="mt-5 flex items-center gap-3 text-slate-900">
                <span className="rounded-xl bg-[#D8EFE6] p-3 text-emerald-800"><Clock className="h-5 w-5" /></span>
                <span>
                  <span className="block font-mono text-[11px] tracking-widest text-slate-400">RESPONSE</span>
                  <span className="block font-display text-sm font-bold">By email, as soon as possible</span>
                </span>
              </div>
            </div>
            <div className="flex items-start gap-3 rounded-xl border border-amber-200 bg-amber-50 p-4">
              <TriangleAlert className="mt-0.5 h-5 w-5 shrink-0 text-amber-600" />
              <p className="text-sm leading-relaxed text-amber-800">
                Enquiry subject: product information / order enquiry. Please avoid sending sensitive medical
                information by email.
              </p>
            </div>
          </div>

          {/* Form column */}
          <div className="rounded-2xl border border-slate-200 bg-white p-7 sm:p-9">
            {status === "sent" ? (
              <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="flex flex-col items-center gap-4 py-14 text-center" data-testid="enquiry-success-message">
                <CheckCircle2 className="h-14 w-14 text-emerald-600" />
                <h2 className="font-display text-2xl font-bold text-slate-900">Enquiry received</h2>
                <p className="max-w-md text-sm leading-relaxed text-slate-600">
                  Thank you for contacting OneCare Pharmacy. Your enquiry has been received. Our team will respond by
                  email as soon as possible.
                </p>
                {reference && (
                  <p className="font-mono text-xs text-slate-400">Reference: {reference}</p>
                )}
                <Link to="/products" data-testid="success-back-catalogue-link" className="mt-2 rounded-full bg-[#0D5C46] px-6 py-3 text-sm font-semibold text-white transition-[background-color] hover:bg-[#0A4837]">
                  Back to catalogue
                </Link>
              </motion.div>
            ) : (
              <form onSubmit={onSubmit} data-testid="enquiry-form" className="space-y-5">
                {items.length > 0 && (
                  <div data-testid="enquiry-basket-panel" className="rounded-xl border border-emerald-200 bg-[#E6F4F0] p-4">
                    <p className="font-display text-sm font-bold text-emerald-900">Products in your enquiry</p>
                    <ul className="mt-3 space-y-2">
                      {items.map((i) => (
                        <li key={i.id} className="flex items-center justify-between gap-2 text-sm text-emerald-950">
                          <span className="truncate">{i.name} {i.strength} <span className="font-mono text-xs text-emerald-700">({i.pack_size})</span></span>
                          <span className="flex items-center gap-2">
                            <span className="font-mono text-xs font-bold">×{i.quantity}</span>
                            <button type="button" data-testid={`basket-remove-${i.id}`} aria-label={`Remove ${i.name}`} onClick={() => removeItem(i.id)} className="rounded-full p-1 text-emerald-700 hover:bg-emerald-200/60">
                              <X className="h-3.5 w-3.5" />
                            </button>
                          </span>
                        </li>
                      ))}
                    </ul>
                    <p className="mt-3 border-t border-emerald-200 pt-2 text-right font-mono text-sm font-bold text-emerald-900">
                      Displayed subtotal: {format(subtotal)}
                    </p>
                  </div>
                )}

                <div className="grid gap-5 sm:grid-cols-2">
                  <label className="block">
                    <span className="mb-1.5 block text-sm font-medium text-slate-700">Full Name</span>
                    <input
                      type="text"
                      required
                      value={form.full_name}
                      onChange={set("full_name")}
                      data-testid="enquiry-name-input"
                      placeholder="Your full name"
                      className="w-full rounded-lg border border-slate-200 px-4 py-3 text-sm text-slate-900 outline-none transition-[border-color] focus:border-emerald-600"
                    />
                  </label>
                  <label className="block">
                    <span className="mb-1.5 block text-sm font-medium text-slate-700">Email Address</span>
                    <input
                      type="email"
                      required
                      value={form.email}
                      onChange={set("email")}
                      data-testid="enquiry-email-input"
                      placeholder="you@example.com"
                      className="w-full rounded-lg border border-slate-200 px-4 py-3 text-sm text-slate-900 outline-none transition-[border-color] focus:border-emerald-600"
                    />
                  </label>
                  <label className="block">
                    <span className="mb-1.5 block text-sm font-medium text-slate-700">Country / Region</span>
                    <select
                      required
                      value={form.country}
                      onChange={set("country")}
                      data-testid="enquiry-country-select"
                      className="w-full rounded-lg border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 outline-none transition-[border-color] focus:border-emerald-600"
                    >
                      <option value="" disabled>Select your country or region</option>
                      {COUNTRIES.map((c) => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </label>
                  <label className="block">
                    <span className="mb-1.5 block text-sm font-medium text-slate-700">Enquiry Type</span>
                    <select
                      value={form.enquiry_type}
                      onChange={set("enquiry_type")}
                      data-testid="enquiry-type-select"
                      className="w-full rounded-lg border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 outline-none transition-[border-color] focus:border-emerald-600"
                    >
                      {TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                    </select>
                  </label>
                </div>

                <label className="block">
                  <span className="mb-1.5 block text-sm font-medium text-slate-700">Message</span>
                  <textarea
                    required
                    minLength={5}
                    rows={6}
                    value={form.message}
                    onChange={set("message")}
                    data-testid="enquiry-message-input"
                    placeholder="How can we help? Include product names, pack sizes or questions."
                    className="w-full resize-y rounded-lg border border-slate-200 px-4 py-3 text-sm leading-relaxed text-slate-900 outline-none transition-[border-color] focus:border-emerald-600"
                  />
                </label>

                <label className="flex items-start gap-3 text-sm text-slate-600">
                  <input
                    type="checkbox"
                    checked={form.consent}
                    onChange={set("consent")}
                    required
                    data-testid="enquiry-consent-checkbox"
                    className="mt-0.5 h-4 w-4 rounded border-slate-300 accent-[#0D5C46]"
                  />
                  <span>I consent to OneCare Pharmacy processing my contact details to respond to this enquiry.</span>
                </label>

                {status === "error" && (
                  <p data-testid="enquiry-error-message" className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                    {error}
                  </p>
                )}

                <button
                  type="submit"
                  data-testid="enquiry-submit-button"
                  disabled={status === "sending"}
                  className="flex w-full items-center justify-center gap-2 rounded-xl bg-[#0D5C46] px-6 py-4 font-display text-sm font-bold text-white transition-[background-color,transform] hover:bg-[#0A4837] active:scale-[0.99] disabled:cursor-not-allowed disabled:opacity-60"
                >
                  <Send className="h-4 w-4" />
                  {status === "sending" ? "Sending enquiry..." : "Send Enquiry"}
                </button>
                <p className="text-center text-xs text-slate-400">
                  Submissions are sent securely to info@onecarelife.health.
                </p>
              </form>
            )}
          </div>
        </div>
      </section>
    </main>
  );
}
